import './AlarmMonitoring.css';
import React, { useRef, useState } from 'react';
import AlarmsByCategories from './AlarmsByCategories';
import EventsService from '../../services/EventsService';
import { useSSEContext } from '../../contexts/sse';
import { useUserStore } from '../../contexts/userStore';
import List from '../../components/List/List';
import { createDateFromFormat, formatDate, isValidDateString } from '../../utils/date';
import { AlarmActions, getAlarmResultText } from '../../components/AlarmActions/AlarmActions';
import Pagination from '../../components/Pagination/Pagination';
import { useTranslation } from 'react-i18next';
import { getEventData } from '../../constants/events';
import { DatetimeWithRelative } from '../../components/DatetimeWithRelative/DatetimeWithRelative';

const AlarmMonitoring = () => {
   const [events, setEvents] = useState([]);
   const [currentPage, setCurrentPage] = useState(1);
   const [maxPage, setMaxPage] = useState(1);
   const [rowsPerPage, setRowsPerPage] = useState(12);
   const [total, setTotal] = useState(0);
   const { sseReceived } = useSSEContext();
   const { selectedCondominio } = useUserStore();
   const [collumnClicked, setCollumnClicked] = useState({
      collumnTitle: "id",
      orderDirection: "DESC",
   });
   const cancelTokenRef = useRef(null);
   const [isLoading, setIsLoading] = useState(true);
   const { t } = useTranslation();

   const getAlarms = async (page, condominiumId, collumnClicked, rowsPerPage) => {
      if (condominiumId === 0) return;
      if (cancelTokenRef.current) {
         cancelTokenRef.current.abort();
      }
      cancelTokenRef.current = new AbortController();
      setIsLoading(true);
      const response = await EventsService.listCondominioEventsPaginated(
         page,
         condominiumId,
         {
            name: "",
            eventType: "Alarme",
            startDate: "",
            endDate: "",
            licence: "",
            detail: "",
            data: "",
            user: "",
            uuid: "",
            result: "",
            info: "",
            carName: "",
            carBrand: "",
            carColor: "",
            carPlate: "",
         },
         collumnClicked,
         true,
         rowsPerPage,
         cancelTokenRef.current.signal,
      );
      setIsLoading(false);
      setCurrentPage(page);
      setEvents(response?.data?.results);
      setMaxPage(Math.ceil(response?.data?.total / rowsPerPage));
      setTotal(response?.data?.total);
   };

   React.useEffect(() => {
      getAlarms(currentPage, selectedCondominio, collumnClicked, rowsPerPage);
   }, [
      selectedCondominio,
      collumnClicked,
      currentPage,
      rowsPerPage,
   ]);

   React.useEffect(() => {
      if (currentPage !== 1) return;
      if (["deviceBatchEventsConfirmation", "deviceBatchEventsDelete"].includes(sseReceived?.action)) {
         getAlarms(1, selectedCondominio, collumnClicked, rowsPerPage);
         return;
      }
      if ("deviceEventConfirmation" === sseReceived?.action) {
         setEvents((prevEvents) => {
            const newEvents = prevEvents.map(event => {
               if (event.id === sseReceived.content.deviceEventId) {
                  return {
                     ...event,
                     alarm_result: sseReceived.content.alarm_result,
                     alarm_action: sseReceived.content.alarm_action,
                     alarm_confirmation: sseReceived.content.alarm_confirmation,
                     alarm_confirmed_by: sseReceived.content.alarm_confirmed_by,
                  };
               }
               return event;
            });
            return newEvents;
         });
         return;
      }
      if (["createEvent"].includes(sseReceived?.action)) {
         if (sseReceived?.content?.type === 'Alarme') {
            setEvents((prevEvents) => {
               const newEvents = [sseReceived.content, ...prevEvents].sort((a, b) => {
                  const aValue = a[collumnClicked.collumnTitle];
                  const bValue = b[collumnClicked.collumnTitle];

                  if (isValidDateString(aValue) && isValidDateString(bValue)) {
                     const aDate = new Date(aValue);
                     const bDate = new Date(bValue);
                     if (collumnClicked.orderDirection === "ASC") {
                        return aDate - bDate;
                     } else {
                        return bDate - aDate;
                     }
                  }
                  if (collumnClicked.orderDirection === "ASC") {
                     return aValue - bValue;
                  } else {
                     return bValue - aValue;
                  }
               });
               if (newEvents.length > rowsPerPage) {
                  newEvents.pop();
               }
               return newEvents;
            });
         }
      }
   }, [
      sseReceived,
   ]);

   return (
      <div className='flex flex-row gap-2.5'>
         <div className='flex-1'>
            <div className="list-container list-visibility list-alarms-table">
               <List
                  isLoading={isLoading}
                  selectableRows={false}
                  handleCollumnTitleClick={(collumnIndex) =>
                     setCollumnClicked(collumnIndex)
                  }
                  collumsObjIndex={[
                     {
                        collumnLabel: t("common.event"),
                        type: "custom",
                        custom: ({ row: alarm }) => {
                           const event = getEventData(alarm.name);
                           return (
                              <span className="flex flex-row items-center gap-1.5!">
                                 {event?.icon ? React.createElement(event.icon, { size: 16, className: 'opacity-40!' }) : null}
                                 <span className="font-semibold!">{t(event?.name) || alarm.name}</span>
                              </span>
                           );
                        }
                     },
                     {
                        collumnLabel: t("common.location"), type: "custom", custom: ({ row: alarm }) => {
                           return (
                              <span className='text-xs!'>
                                 {alarm.target_sector && alarm.target_sector !== "" ? alarm.target_sector : (alarm.target_name ? (<span>{alarm.unit?.name ? alarm.unit.name + '/' : ''} {alarm.target_name}</span>) : (<small style={{ opacity: 0.36 }}>{t("common.doesNotContain")}</small>))}
                              </span>
                           );
                        }
                     },
                     { collumnLabel: t("common.date"), type: "custom", custom: ({ row: alarm }) => <span className='text-xs!'><DatetimeWithRelative datetime={alarm.fulldate} /></span> },
                     {
                        collumnLabel: t("common.confirmed"),
                        type: "custom",
                        custom: ({ row: alarm }) => {
                           return (
                              <span className='flex flex-col gap-1'>
                                 <span className='text-xs!'>{alarm.alarm_confirmation ? t("common.yes") + ` ${alarm.alarm_result ? " / " + t(getAlarmResultText(alarm.alarm_result)) : ""}` : (alarm.alarm_confirmation != null ? t("common.false") + ` ${alarm.alarm_result ? " / " + t(getAlarmResultText(alarm.alarm_result)) : ""}` : <span style={{ opacity: 0.36 }}>{t("common.no")}</span>)}</span>
                              </span>
                           );
                        }
                     },
                     {
                        collumnLabel: t("common.confirmedBy"), type: "custom", custom: ({ row: alarm }) => {
                           if (!alarm.alarm_confirmed_by) return <span style={{ opacity: 0.36 }}>{alarm.alarm_confirmation || alarm.alarm_result ? t("common.anonymous") : t("common.nobody")}</span>;
                           const [name, email, uuid] = alarm.alarm_confirmed_by.split("%");
                           return (
                              <span className='flex flex-col gap-1'>
                                 <span className='text-xs!'>{name}</span>
                                 {alarm.alarm_confirmed_at && (
                                    <span className='text-xs!'>
                                       <DatetimeWithRelative datetime={alarm.alarm_confirmed_at} />
                                    </span>
                                 )}
                              </span>
                           );
                        }
                     },
                     {
                        collumnLabel: t("common.action"), type: "actions", custom: ({ row: alarm }) =>
                           <div className='flex justify-end flex-row w-full'>
                              {alarm.alarm_action && alarm.alarm_action !== "" ? alarm.alarm_action : alarm.alarm_confirmation != null ? <div className='min-h-[28px] flex items-center opacity-36'>{t("common.doesNotContain")}</div> : <AlarmActions alarm={alarm} />}
                           </div>
                     },
                  ]}
                  data={events?.map(event => {
                     const updatedEvent = { ...event };
                     if (event.name.includes('panico') || event.name.includes('violada')) {
                        updatedEvent._listLineClassName_ = "list-line-warning";
                     }
                     return updatedEvent;
                  })}
               />
            </div>
            <Pagination
               rowsPerPage={rowsPerPage}
               maxPage={maxPage}
               total={total}
               changePage={(page) =>
                  setCurrentPage(page)
               }
               changeRowsPerPage={(rowsPerPage) =>
                  setRowsPerPage(rowsPerPage)
               }
            />
         </div>
         <AlarmsByCategories />
      </div>
   );
};

export default AlarmMonitoring;
